import { Vector3 } from 'three';
import { SweepMesh } from '../geometry/sweep';
import { TrackSupportPrimitive } from '../supports/types';
/** Footers closer than this (m) are treated as belonging to a column endpoint. */
export const FOOTER_MATCH_M = 0.55;
export const HUB_MATCH_M = 0.4;

const CYL_SEGMENTS = 12;

type FooterPad = Pick<Extract<TrackSupportPrimitive, { kind: 'footer' }>, 'pos' | 'radius' | 'height'>;

/**
 * True when a joint sphere sits in/on a ground pad or would bulge below its
 * flat underside. Footers are glue faces — never ball joints.
 */
export function jointOnFooterPad(
  pos: Vector3,
  radius: number,
  footers: FooterPad[],
): boolean {
  const r = Math.max(radius, 0);
  for (const f of footers) {
    const horiz = Math.hypot(pos.x - f.pos.x, pos.z - f.pos.z);
    const padR = Math.max(f.radius, FOOTER_MATCH_M);
    if (horiz > padR + r) continue;
    const bottom = f.pos.y;
    const top = f.pos.y + Math.max(f.height, 0.12);
    // Hemisphere (or more) sticking out under the pad.
    if (pos.y - r < bottom - 1e-4 && horiz <= padR) return true;
    // Center in the pad volume, including the glue plane and pad top.
    if (horiz <= padR && pos.y <= top + 1e-3 && pos.y >= bottom - Math.max(r, HUB_MATCH_M)) {
      return true;
    }
  }
  return false;
}

function pointOnFooterPad(pos: Vector3, footers: FooterPad[]): boolean {
  return jointOnFooterPad(pos, 0, footers);
}

/** Ensure every brace/knee endpoint has a joint sphere hub (diameter matches beam). */
export function ensureBeamJointHubs(prims: TrackSupportPrimitive[]): TrackSupportPrimitive[] {
  const footers = prims.filter((p) => p.kind === 'footer');
  // Drop existing balls on/under pads so print slices keep a flat footer underside.
  const stripped = prims.filter(
    (p) => p.kind !== 'joint' || !jointOnFooterPad(p.pos, p.radius, footers),
  );
  const hubs: Vector3[] = [];
  for (const p of stripped) {
    if (p.kind === 'joint') hubs.push(p.pos.clone());
  }
  const out = [...stripped];
  const addHub = (pos: Vector3, radius: number) => {
    if (jointOnFooterPad(pos, radius, footers)) return;
    if (hubs.some((h) => h.distanceTo(pos) < HUB_MATCH_M)) return;
    hubs.push(pos.clone());
    // Sphere diameter matches beam diameter (same diagonal of the tube cross-section).
    out.push({ kind: 'joint', pos: pos.clone(), radius: Math.max(radius, 1e-4) });
  };
  for (const p of stripped) {
    if (p.kind === 'brace' || p.kind === 'knee') {
      addHub(p.a, p.radius);
      addHub(p.b, p.radius);
    }
  }
  return dedupeJointHubs(out);
}

/**
 * Collapse overlapping joint spheres (support gen often emits one per member end).
 * Duplicate solid hubs unioned without a shared cavity fill female sockets shut.
 */
export function dedupeJointHubs(prims: TrackSupportPrimitive[]): TrackSupportPrimitive[] {
  const joints: Extract<TrackSupportPrimitive, { kind: 'joint' }>[] = [];
  const rest: TrackSupportPrimitive[] = [];
  for (const p of prims) {
    if (p.kind === 'joint') joints.push(p);
    else rest.push(p);
  }
  const kept: Extract<TrackSupportPrimitive, { kind: 'joint' }>[] = [];
  for (const j of joints) {
    const existing = kept.find((k) => k.pos.distanceTo(j.pos) < HUB_MATCH_M);
    if (!existing) {
      kept.push({ kind: 'joint', pos: j.pos.clone(), radius: j.radius });
    } else {
      existing.radius = Math.max(existing.radius, j.radius);
    }
  }
  return [...rest, ...kept];
}

/**
 * Clip columns/braces so they sit on top of footers (flat glue face), never under them.
 */
export function prepareSupportPrimitives(
  prims: TrackSupportPrimitive[],
): TrackSupportPrimitive[] {
  const footers = prims.filter((p) => p.kind === 'footer') as Extract<
    TrackSupportPrimitive,
    { kind: 'footer' }
  >[];

  return prims.map((prim) => {
    if (prim.kind === 'footer' || prim.kind === 'joint') return prim;
    const radius =
      prim.kind === 'stub' ? Math.max(prim.radiusA, prim.radiusB) : prim.radius;
    return {
      ...prim,
      a: clipAwayFromFooters(prim.a, prim.b, footers, radius),
      b: clipAwayFromFooters(prim.b, prim.a, footers, radius),
    };
  });
}

/** If `point` sits in/under a footer pad, raise it to the pad top along the member axis. */
function clipAwayFromFooters(
  point: Vector3,
  other: Vector3,
  footers: Extract<TrackSupportPrimitive, { kind: 'footer' }>[],
  radius: number,
): Vector3 {
  let out = point.clone();
  for (const f of footers) {
    const dx = point.x - f.pos.x;
    const dz = point.z - f.pos.z;
    const horiz = Math.hypot(dx, dz);
    if (horiz > Math.max(f.radius, FOOTER_MATCH_M) + Math.max(radius, 0)) continue;
    const topY = f.pos.y + Math.max(f.height, 0.12);
    if (out.y >= topY - 1e-6) continue;
    out = raiseToPlaneY(out, other, topY);
  }
  return out;
}

/** True when a member endpoint sits on a footer pad top (glue face). */
export function endOnFooterPad(pos: Vector3, footers: FooterPad[]): boolean {
  return pointOnFooterPad(pos, footers);
}

function raiseToPlaneY(point: Vector3, other: Vector3, planeY: number): Vector3 {
  if (point.y >= planeY - 1e-9) return point.clone();
  const dy = other.y - point.y;
  if (Math.abs(dy) < 1e-9) return new Vector3(point.x, planeY, point.z);
  const t = (planeY - point.y) / dy;
  return point.clone().lerp(other, Math.min(1, Math.max(0, t)));
}

export interface CylinderMeshOpts {
  /** Slice end A with a horizontal plane at `a.y` (footer glue face). */
  horizontalCapA?: boolean;
  /** Slice end B with a horizontal plane at `b.y` (footer glue face). */
  horizontalCapB?: boolean;
}

/** Flat-bottom footer pad — underside is planar for gluing / placing. */
export function flatFooterMesh(pos: Vector3, radius: number, height: number): SweepMesh {
  const h = Math.max(height, 0.12);
  const r = Math.max(radius, 0.2);
  const bottom = new Vector3(pos.x, pos.y, pos.z);
  const top = new Vector3(pos.x, pos.y + h, pos.z);
  return cylinderMesh(bottom, top, r * 1.05, r);
}

export function cylinderMesh(
  a: Vector3,
  b: Vector3,
  radiusA: number,
  radiusB: number,
  opts?: CylinderMeshOpts,
): SweepMesh {
  const dir = new Vector3().subVectors(b, a);
  const len = Math.max(dir.length(), 1e-6);
  dir.multiplyScalar(1 / len);

  const up = Math.abs(dir.y) < 0.9 ? new Vector3(0, 1, 0) : new Vector3(1, 0, 0);
  const right = new Vector3().crossVectors(dir, up).normalize();
  const forward = new Vector3().crossVectors(right, dir).normalize();

  const rings = 2;
  const seg = CYL_SEGMENTS;
  const positions = new Float32Array((rings * seg + 2) * 3);
  const normals = new Float32Array(positions.length);
  const indices: number[] = [];
  const horizontal = [opts?.horizontalCapA === true, opts?.horizontalCapB === true];

  for (let r = 0; r < rings; r++) {
    const radius = r === 0 ? radiusA : radiusB;
    const center = new Vector3().copy(a).addScaledVector(dir, r * len);
    for (let i = 0; i < seg; i++) {
      const ang = (i / seg) * Math.PI * 2;
      const nx = Math.cos(ang);
      const nz = Math.sin(ang);
      const offset = new Vector3()
        .copy(right)
        .multiplyScalar(nx * radius)
        .addScaledVector(forward, nz * radius);
      const p = ringVertex(center, offset, dir, horizontal[r]);
      const vi = (r * seg + i) * 3;
      positions[vi] = p.x;
      positions[vi + 1] = p.y;
      positions[vi + 2] = p.z;
      const nn = new Vector3()
        .copy(right)
        .multiplyScalar(nx)
        .addScaledVector(forward, nz)
        .normalize();
      normals[vi] = nn.x;
      normals[vi + 1] = nn.y;
      normals[vi + 2] = nn.z;
    }
  }

  for (let i = 0; i < seg; i++) {
    const i1 = (i + 1) % seg;
    indices.push(i, seg + i, i1, i1, seg + i, seg + i1);
  }

  const bot = rings * seg;
  const top = bot + 1;
  positions[bot * 3] = a.x;
  positions[bot * 3 + 1] = a.y;
  positions[bot * 3 + 2] = a.z;
  const aN = horizontal[0] ? new Vector3(0, a.y <= b.y ? -1 : 1, 0) : dir.clone().multiplyScalar(-1);
  normals[bot * 3] = aN.x;
  normals[bot * 3 + 1] = aN.y;
  normals[bot * 3 + 2] = aN.z;
  positions[top * 3] = b.x;
  positions[top * 3 + 1] = b.y;
  positions[top * 3 + 2] = b.z;
  const bN = horizontal[1] ? new Vector3(0, b.y <= a.y ? -1 : 1, 0) : dir.clone();
  normals[top * 3] = bN.x;
  normals[top * 3 + 1] = bN.y;
  normals[top * 3 + 2] = bN.z;

  for (let i = 0; i < seg; i++) {
    const i1 = (i + 1) % seg;
    indices.push(bot, i1, i);
    indices.push(top, seg + i, seg + i1);
  }

  return { positions, normals, indices: Uint32Array.from(indices) };
}

/**
 * Perp-tube ring vertex, optionally slid along the axis onto the horizontal
 * plane through `center` so a leaning beam ends flush on a footer pad.
 */
function ringVertex(
  center: Vector3,
  offset: Vector3,
  dir: Vector3,
  horizontal: boolean,
): Vector3 {
  const p = center.clone().add(offset);
  if (!horizontal) return p;
  if (Math.abs(dir.y) < 0.08) {
    p.y = Math.max(p.y, center.y);
    return p;
  }
  const t = (center.y - p.y) / dir.y;
  return p.addScaledVector(dir, t);
}

export function meshPoints(mesh: SweepMesh): Vector3[] {
  const pts: Vector3[] = [];
  for (let i = 0; i < mesh.positions.length; i += 3) {
    pts.push(new Vector3(mesh.positions[i], mesh.positions[i + 1], mesh.positions[i + 2]));
  }
  return pts;
}
